import {
  prisma,
  type Prisma,
  ResourceType,
  ActionType,
} from "client";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";

async function invalidateEventPageCacheForVenueEvents(eventIds: string[]) {
  const uniqueEventIds = [...new Set(eventIds.filter(Boolean))];

  await Promise.all(
    uniqueEventIds.map(async (eventId) => {
      const event = await prisma.event.findUnique({
        where: { id: eventId },
        select: { slug: true },
      });
      await invalidateEventPageCacheForEvent(eventId, event?.slug);
    })
  );
}

// READ

export const getVenueById = async (userId: string, id: string) => {
  try {
    const venue = await prisma.venue.findUnique({
      where: { id },
      include: {
        translations: true,
        floorPlans: {
          where: {
            deletedAt: null,
          },
          include: {
            formEntities: {
              include: {
                formTemplate: {
                  include: {
                    translations: true,
                  },
                },
              },
            },
          },
        },
      },
    });

    if (!venue) {
      return { error: "Venue not found" };
    }

    return venue;
  } catch (error) {
    return { error };
  }
};

export const getVenueByIdWithoutPermissions = async (id: string) => {
  try {
    const venue = await prisma.venue.findUnique({
      where: { id },
      include: {
        translations: true,
        floorPlans: {
          where: {
            deletedAt: null,
          },
        },
      },
    });

    if (!venue) {
      return { error: "Venue not found" };
    }

    return venue;
  } catch (error) {
    return { error };
  }
};

export const getVenueByOrganizationId = async (
  userId: string,
  organizationId: string
) => {
  try {
    const venues = await prisma.venue.findMany({
      where: { organizationIds: { has: organizationId }, deletedAt: null },
    });

    return venues;
  } catch (error) {
    return { error };
  }
};

export const getVenueByWhiteLabelId = async (
  userId: string,
  whiteLabelId: string
) => {
  try {
    const venues = await prisma.venue.findMany({
      where: { whiteLabelId, deletedAt: null },
    });

    return venues;
  } catch (error) {
    return { error };
  }
};

export const getVenueByEventId = async (
  userId: string,
  eventId: string,
  local?: string
) => {
  try {
    const venue = await prisma.venue.findMany({
      where: { eventIds: { has: eventId }, deletedAt: null },
      include: {
        translations: {
          include: {
            language: true,
          },
        },
        zones: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        floorPlans: {
          where: {
            deletedAt: null,
          },
        },
      },
    });

    return venue?.map((venue) => ({
      ...venue,
      ...venue.translations?.find(
        (translation) => translation.language.code === local
      ),
      zones: venue.zones?.map((zone) => ({
        ...zone,
        ...zone.translations?.find(
          (translation) => translation.language.code === local
        ),
        id: zone.id,
      })),
      id: venue.id,
    }));
  } catch (error) {
    return { error };
  }
};

// CREATE

export const createVenue = async (
  userId: string,
  venue: Prisma.VenueCreateInput,
  eventId: string,
  translations: Prisma.VenueTranslationsCreateManyInput[]
) => {
  try {
    const newVenue = await prisma.venue.create({
      data: {
        ...venue,
        createdBy: {
          connect: {
            id: userId,
          },
        },
        deletedAt: null,
        events: {
          connect: {
            id: eventId,
          },
        },
        translations: {
          createMany: {
            data: translations,
          },
        },
      },
    });
    await invalidateEventPageCacheForVenueEvents(newVenue.eventIds);

    return newVenue;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// UPDATE

export const updateVenue = async (
  userId: string,
  id: string,
  venue: Prisma.VenueUpdateInput,
  translations: Prisma.VenueTranslationsCreateManyInput[]
) => {
  try {
    const updatedVenue = await prisma.venue.update({
      where: { id },
      data: {
        ...venue,
        translations: {
          deleteMany: {},
          createMany: {
            data: translations,
          },
        },
      },
    });

    await invalidateEventPageCacheForVenueEvents(updatedVenue.eventIds);

    return updatedVenue;
  } catch (error) {
    return { error };
  }
};

// DELETE

export const deleteVenue = async (userId: string, id: string) => {
  try {
    const deletedVenue = await prisma.venue.update({
      where: { id },
      data: { deletedAt: new Date() },
    });

    await invalidateEventPageCacheForVenueEvents(deletedVenue.eventIds);

    return deletedVenue;
  } catch (error) {
    return { error };
  }
};

export const getVenuesCountByOrganizationId = async (
  organizationId: string
) => {
  try {
    const venuesCount = await prisma.venue.count({
      where: {
        events: { some: { organizationIds: { has: organizationId } } },
        deletedAt: null,
      },
    });
    return venuesCount;
  } catch (error) {
    return { error };
  }
};
